// Import the pure types module (not the package barrel) so this stays importable
// from Vitest without dragging the chat runtime in.
import { isAskUserQuestionStagedAction } from '@renderer/chat/integrations/ask-user-question/types'
import { isAskUserFeedbackStagedAction } from '@renderer/chat/integrations/ask-user-feedback/types'
// Same reason as the imports above: pull the predicate from the status module
// directly rather than the hooks barrel, which drags the query layer in.
import { isEditableActionStatus } from '../../hooks/staged-action-status'
import type { ActionStatus } from '../../hooks/use-staged-actions'
import type { ActionEditState } from '../../atoms'

/**
 * The Action View's designed states (HIG-5824, `sent` added by HIG-5831):
 * - `ready` — the drafted action with chips, body, and the execute footer.
 *   `invoking` (execute in flight), `error` (retry) and `archived` (restore)
 *   render as `ready` too, with the footer adapting to the status.
 * - `question` — a background AskUserQuestion needing the user's input.
 * - `feedback` — a background AskUserFeedback: an open question the user can
 *   answer or skip. Its own state rather than a flavour of `question` because
 *   nothing is blocked on it — the work already shipped — and the chrome says
 *   so: no purple "needs you" glow, and skipping is a first-class outcome.
 * - `updating` — a scoped-chat edit is regenerating the draft.
 * - `sent` — the action has run: content preserved, no standing glow, and a
 *   footer of undo + open-the-output instead of the execute CTA.
 */
export type ActionViewState = 'ready' | 'question' | 'feedback' | 'updating' | 'sent'

interface DeriveActionViewStateInput {
  action: { toolSet?: string; toolName?: string }
  status: ActionStatus
  editState: ActionEditState | undefined
}

/**
 * Order matters: an in-flight edit shows Updating even for a question action
 * (the regeneration is what the user just asked for), and an answered question
 * stays in `question` so the card can render its resolved read-only state.
 * `sent` sits below both — answering a question *is* its execution, so a
 * resolved question keeps the card rather than picking up the sent footer.
 */
export function deriveActionViewState({ action, status, editState }: DeriveActionViewStateInput): ActionViewState {
  if (editState?.status === 'pending' && isEditableActionStatus(status)) {
    return 'updating'
  }
  // Archiving retires every kind of staged action. In particular, a question
  // or feedback action must hand its answer card back to the standard controls
  // so Restore is available instead of continuing to ask for input.
  if (status === 'archived') {
    return 'ready'
  }
  if (isAskUserQuestionStagedAction(action)) {
    return 'question'
  }
  // Same reasoning as `question` above: answering IS this action’s execution,
  // so an answered one keeps its card rather than picking up the sent footer.
  if (isAskUserFeedbackStagedAction(action)) {
    return 'feedback'
  }
  // A failed undo counts as sent: the remote resource still exists, so the
  // footer needs Retry Undo and the output link, not the execute CTA.
  if (status === 'pending' || status === 'completed' || status === 'undo-error') {
    return 'sent'
  }
  return 'ready'
}

// Figma maps Ready/Question to yellow, Feedback to blue, and Updating or
// unsent edits to green. Sent and archived use the neutral fade — they are
// no longer a live draft.
const GLOW_COLORS: Record<Exclude<ActionViewState, 'sent'>, string> = {
  ready: 'rgba(249, 243, 61, 0.2)',
  question: 'rgba(249, 243, 61, 0.2)',
  feedback: 'rgba(0, 121, 255, 0.2)',
  updating: 'rgba(81, 218, 120, 0.2)',
}

export function actionViewBottomFadeTint({
  status,
  state,
  isDirty,
}: {
  status: ActionStatus
  state: ActionViewState
  isDirty: boolean
}): string | undefined {
  if (status === 'archived' || state === 'sent') return undefined
  if (state === 'ready' && isDirty) return GLOW_COLORS.updating
  return GLOW_COLORS[state]
}

/**
 * The glow a chat surface wears while hoisting a pending answerable above its
 * composer, the same colour the Action View gives that card. Callers pass an
 * action already known to be pending (`isPendingAnswerableStagedAction`).
 */
export function pendingAnswerableGlowTint(action: { toolSet?: string; toolName?: string }): string {
  return isAskUserFeedbackStagedAction(action) ? GLOW_COLORS.feedback : GLOW_COLORS.question
}
